import {
  TPairComparisonInstances,
  TPairComparisonSession,
  TResutsPerSequence,
  TSequence,
} from "../types/pairComparison";
import * as XLSX from "xlsx";
import { getColumnWidths, getFeedbacksSheet, createInfoSheet } from "./common";
import * as _ from "lodash";
import { TParticipantVotes } from "types/common";

export const exportPairComparisonResults = (
  session: TPairComparisonSession,
  instances: TPairComparisonInstances
) => {
  const excelFile = XLSX.utils.book_new();

  // Créer l'onglet d'informations générales
  const infoSheet = createInfoSheet(
    session.metadata.name,
    "Pair Comparison",
    session.metadata.context
  );
  XLSX.utils.book_append_sheet(excelFile, infoSheet, "Information");

  const feedbacksSheet = getFeedbacksSheet(session, instances);
  const { resutsPerSequences, resourceStats } = getStatsData(
    session,
    instances
  );

  // Créer un onglet pour chaque séquence
  const resultsBySequence = _.groupBy(resutsPerSequences, "sequenceId");
  Object.keys(resultsBySequence).forEach((sequenceId, index) => {
    const sequenceResults = resultsBySequence[sequenceId];
    const resultsSheet = createResultsSheet(sequenceResults);

    if (resultsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        resultsSheet,
        `Sequence ${index + 1}`
      );
    }
  });

  // Créer l'onglet Statistiques
  const statisticsSheet = createStatisticsSheet(resourceStats, session);
  if (statisticsSheet) {
    XLSX.utils.book_append_sheet(excelFile, statisticsSheet, "Statistics");
  }

  if (feedbacksSheet) {
    XLSX.utils.book_append_sheet(excelFile, feedbacksSheet, "Feedbacks");
  }

  XLSX.writeFile(excelFile, `export_session_${session.id}.xlsx`);
};

const createResultsSheet = (resutsPerSequences: TResutsPerSequence[]) => {
  if (!resutsPerSequences || resutsPerSequences.length === 0) {
    return null;
  }

  const excelRows = [];
  const allHeaders = [...Object.keys(resutsPerSequences[0] || {})];
  // Exclure sequenceId de l'affichage
  const headers = allHeaders.filter((header) => header !== "sequenceId");
  excelRows.push(headers);

  resutsPerSequences.forEach((row: any) => {
    const rowData = headers.map((header) => row[header] || "");
    excelRows.push(rowData);
  });

  const resultsSheet = XLSX.utils.aoa_to_sheet(excelRows);
  const colWidths = getColumnWidths(resutsPerSequences);
  resultsSheet["!cols"] = colWidths;

  return resultsSheet;
};

const createStatisticsSheet = (
  resourceStats: any[],
  session: TPairComparisonSession
) => {
  if (!resourceStats || resourceStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // En-têtes de base
  const baseHeaders = ["Ressource", "Average", "Median", "Standard deviation"];

  // Ajouter les colonnes d'attributs
  const attributeHeaders =
    session.metadata.attributes?.map((attr) => `${attr.name} - ${attr.unit}`) ||
    [];
  const allHeaders = [...baseHeaders, ...attributeHeaders];

  excelRows.push(allHeaders);

  // Données
  resourceStats.forEach((stat) => {
    const baseData = [
      stat.resourceName,
      stat.average,
      stat.median,
      stat.standardDeviation,
    ];

    // Ajouter les valeurs des attributs
    const attributeValues =
      session.metadata.attributes?.map((attr, index) => {
        const resource = session.data.sequencesResources.find(
          (r) => r.name === stat.resourceName
        );
        return resource?.attributes?.[index]?.quantity || "";
      }) || [];

    const allData = [...baseData, ...attributeValues];
    excelRows.push(allData);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Ajuster la largeur des colonnes
  const colWidths = [
    { width: 30 }, // Ressource
    { width: 15 }, // Average
    { width: 15 }, // Median
    { width: 15 }, // Standard deviation
  ];

  // Ajouter les largeurs pour les colonnes d'attributs
  attributeHeaders.forEach(() => {
    colWidths.push({ width: 20 });
  });

  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};

const getStatsData = (
  session: TPairComparisonSession,
  instances: TPairComparisonInstances
) => {
  const results: TParticipantVotes = {};

  instances.map((instance) => _.merge(results, instance.results));
  const { data, metadata } = session;
  const { sequences = [] } = data;
  const { personalQuestions = [] } = metadata;
  const resutsPerSequences: TResutsPerSequence[] = [];
  const resourceStats: any[] = [];

  const participantNames = metadata.participants
    ? Object.values(metadata.participants).map((obj) => obj.participantName)
    : [];

  // Initialiser les statistiques pour chaque ressource
  data.sequencesResources.forEach((resource) => {
    resourceStats.push({
      resourceId: resource.id,
      resourceName: resource.name,
      points: [],
    });
  });

  for (var sequenceId in results) {
    // ignore next line
    // @ts-ignore
    const sequenceVotes = _.omit(results[sequenceId], "_fakeId");

    // eslint-disable-next-line
    const sequence = sequences.find((s) => s.id === sequenceId);

    let participantIndex = 0;
    for (var participantId in sequenceVotes) {
      // @ts-ignore
      const vote = sequenceVotes[participantId];

      const participantName =
        participantNames[participantIndex] || participantId;

      const resultRow: any = {
        sequenceId: sequenceId,
        Participant: participantName,
        A: sequence?.resources[0].name,
        B: sequence?.resources[1].name,
        Vote: getWinnerResource(vote, sequence),
      };

      // Add personal questions columns
      personalQuestions.forEach((question) => {
        const instance = instances.find((inst) =>
          inst.participants?.find((p) => p.id === participantId)
        );

        const answer = instance?.personalQuestions?.[question.id] || "";
        resultRow[question.question] = answer;
      });

      resutsPerSequences.push(resultRow);
      participantIndex++;

      // Collecter les points pour les statistiques
      resourceStats.forEach((resource) => {
        if (resource.resourceId === vote) {
          resource.points.push(2); // Ressource choisie
        } else if (vote === "null") {
          resource.points.push(1); // Égalité
        } else {
          // Vérifier si cette ressource était dans cette séquence
          const sequence = sequences.find((s) => s.id === sequenceId);
          if (
            sequence &&
            sequence.resources.some((r) => r.id === resource.resourceId)
          ) {
            resource.points.push(0); // Ressource non choisie
          }
        }
      });
    }
  }

  // Calculer les statistiques finales
  const finalStats = resourceStats.map((resource) => {
    const points = resource.points;
    const average =
      points.length > 0
        ? points.reduce((sum: number, point: number) => sum + point, 0) /
          points.length
        : 0;

    // Calcul de la médiane
    const sortedPoints = [...points].sort((a, b) => a - b);
    const median =
      sortedPoints.length > 0
        ? sortedPoints.length % 2 === 0
          ? (sortedPoints[sortedPoints.length / 2 - 1] +
              sortedPoints[sortedPoints.length / 2]) /
            2
          : sortedPoints[Math.floor(sortedPoints.length / 2)]
        : 0;

    // Calcul de l'écart-type
    const variance =
      points.length > 0
        ? points.reduce(
            (sum: number, point: number) => sum + Math.pow(point - average, 2),
            0
          ) / points.length
        : 0;
    const standardDeviation = Math.sqrt(variance);

    return {
      resourceName: resource.resourceName,
      average: Math.round(average * 100) / 100, // Arrondir à 2 décimales
      median: Math.round(median * 100) / 100, // Arrondir à 2 décimales
      standardDeviation: Math.round(standardDeviation * 100) / 100, // Arrondir à 2 décimales
    };
  });

  return { resutsPerSequences, resourceStats: finalStats };
};

const getWinnerResource = (vote: string, sequence: TSequence | undefined) => {
  if (vote === sequence?.resources[0].id) {
    return sequence?.resources[0].name;
  } else if (vote === sequence?.resources[1].id) {
    return sequence?.resources[1].name;
  } else {
    return "null";
  }
};
